import React from "react"
import { ImageFromJSONTyped, Item } from "../../libs/api/backend/src"
import { DefaultImageUrl } from "../../libs/vars/url"
import Modal from "../Elements/Modal"
import { formatNumber, formatPriceKZT } from "@malond/utils"
import { cn } from "@malond/ui/utils"
import { X } from "lucide-react"

interface Props {
  items: Array<Item>
  onClickItem: (item: Item) => void
}

function ItemStackedList({ items, onClickItem }: Props) {
  const [selectedImage, setSelectedImage] = React.useState<{
    url: string
    title: string
    barcode?: string
  } | null>(null)

  const cancelButtonRef = React.useRef(null)

  return (
    <div className="overflow-hidden">
      <ul role="list" className="divide-y divide-border">
        {items.map((item) => {
          const itemImage = ImageFromJSONTyped(item.image, false)
          const hasDiscount = item.priceOut !== item.priceDiscount

          return (
            <li
              key={item.id}
              className="block w-full transition-colors hover:cursor-pointer hover:bg-muted/50"
              onClick={() => onClickItem(item)}
            >
              <div className="flex w-full flex-row items-center px-4 py-3 sm:px-6">
                {/* Left side */}
                <div className="flex basis-1/2 flex-row items-center truncate">
                  {/* Image Container with Tappable Margins */}
                  <button
                    type="button"
                    className="mr-2 flex-none rounded-lg p-1.5 transition-colors hover:bg-muted focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation()
                      const imageUrl =
                        (itemImage &&
                          (itemImage.urlMedium ||
                            itemImage.urlSmall ||
                            itemImage.urlThumbnail)) ||
                        DefaultImageUrl
                      setSelectedImage({
                        url: imageUrl,
                        title: item.title,
                        barcode: item.barcode,
                      })
                    }}
                  >
                    <img
                      className="h-12 w-12 rounded-md border border-border bg-muted object-cover"
                      src={
                        (itemImage && itemImage.urlThumbnail) || DefaultImageUrl
                      }
                      alt={item.title}
                    />
                  </button>

                  {/* Title */}
                  <div className="basis-1/2 truncate">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {item.title}
                    </p>

                    <p className="text-xs text-muted-foreground">
                      {item.barcode}
                    </p>

                    {item.category?.title && (
                      <p className="truncate text-xs text-muted-foreground">
                        {item.category.title}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right side */}
                <div className="flex basis-1/2 flex-row items-center">
                  {/* Count */}
                  <div className="basis-1/2 px-4 text-right">
                    <p className="text-sm font-medium text-muted-foreground">
                      × {formatNumber(item.count)}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="basis-1/2 text-right">
                    <p
                      className={cn("truncate text-sm", {
                        "font-bold text-foreground": !hasDiscount,
                        "text-xs text-muted-foreground line-through":
                          hasDiscount,
                      })}
                    >
                      {formatPriceKZT(item.priceOut)}
                    </p>

                    {hasDiscount && (
                      <p className="truncate text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        {formatPriceKZT(item.priceDiscount)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          setOpen={() => setSelectedImage(null)}
          cancelButtonRef={cancelButtonRef}
        >
          <div className="relative flex flex-col items-center bg-card p-1">
            {/* Close button */}
            <button
              ref={cancelButtonRef}
              type="button"
              className="absolute top-3 right-3 z-10 cursor-pointer rounded-full bg-background/80 p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:ring-2 focus:ring-ring focus:outline-none"
              onClick={() => setSelectedImage(null)}
            >
              <X className="size-5" />
            </button>

            {/* Image */}
            <div className="flex min-h-[300px] w-full items-center justify-center overflow-hidden rounded-t-lg bg-muted/40 p-2">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain select-none"
              />
            </div>

            {/* Title and details */}
            <div className="w-full rounded-b-lg border-t border-border bg-card px-4 py-3 text-card-foreground">
              <h3 className="truncate text-base font-semibold text-foreground">
                {selectedImage.title}
              </h3>
              {selectedImage.barcode && (
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {selectedImage.barcode}
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ItemStackedList
